import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Progress,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Search,
  Bookmark,
  Sparkles,
  Activity,
} from 'lucide-react';
import { useCompleteTour } from '@/api/mutations/useOnboardingMutations';

const MotionBox = motion.create(Box);

interface TourStep {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  route: string;
  icon: React.ReactNode;
}

const tourSteps: TourStep[] = [
  {
    id: 'feed',
    title: 'Stay in The Loop',
    subtitle: 'Social Feed',
    description:
      'See what your friends are eating,\nshare your favorite lists and\ndiscover new restaurants through\nyour social network.',
    route: '/feed',
    icon: <Activity size={32} />,
  },
  {
    id: 'search',
    title: 'Know Before You Go',
    subtitle: 'Restaurant Compatibility',
    description:
      'Search for restaurants by location,\ncuisine or menu item and see your\nTasteMatch™ with each before you go.\n\nTap a restaurant card to open its\nfull profile. Rate, Save and\nShare on the card are quick actions\nthat keep you right where you are.\n\nThe stronger the match, the\nmore likely you are to love it!',
    route: '/search',
    icon: <Search size={32} />,
  },
  {
    id: 'lists',
    title: 'Stay Organized',
    subtitle: 'Interactive Lists',
    description:
      'Save restaurants to one of\nyour fully customizable lists.\n\nCreate new lists for any & all\noccasions or locations like:\n"Date Night Spots", "Tampa Bucketlist",\n"Philly Brunch Spots" and plenty more.\n\nSharing lists makes them part of\nthe Community Lists collection\nthat are searchable by creator,\nrestaurant name or location.\n\nInvite friends to collaborate\non Group Lists and vote on\nwhere you\'ll meet up next.',
    route: '/lists',
    icon: <Bookmark size={32} />,
  },
  {
    id: 'quizzes',
    title: 'Compatibility is Personal',
    subtitle: 'PersonalTaste Profile',
    description:
      'Take fun food quizzes & privately rate\nrestaurants to start building your\nPersonalTaste™ profile.\n\nThe more you do — the more you\'ll\nrefine your PersonalTaste™ & improve\nthe reliability of your TasteMatches™.',
    route: '/quizzes',
    icon: <Sparkles size={32} />,
  },
];

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * OnboardingTour component - Step-by-step guided tour overlay
 *
 * Shows users the key features of the app with navigation to each section.
 * Can be triggered after quiz completion or from the menu.
 */
export function OnboardingTour({ isOpen, onClose }: OnboardingTourProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: completeTour } = useCompleteTour();
  const [currentStep, setCurrentStep] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right'>('right');
  const wasOpenRef = useRef(false);
  // True between a pointer-down on an arrow control and the next pointer-down
  // anywhere else. The overlay is a drag="x" surface, so a quick flick on an
  // arrow can both fire the arrow's click AND end a drag with enough velocity
  // to count as a swipe — advancing two steps. While this is set, handleDragEnd
  // ignores the gesture.
  const arrowPressRef = useRef(false);

  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? true;

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  // Keep the tour's route in sync with the current step. When the tour just
  // opened, start at whichever step matches the member's current location
  // (e.g. completing the quiz navigates to /search first — the tour should
  // open on the Search step, not force the member back to Feed) instead of
  // always resetting to step 0 and navigating away from wherever the app
  // just sent them. Falls back to step 0 if the current route isn't one of
  // the tour's stops. On later step changes (Next/Prev/dot navigation) it
  // navigates to that step's route as before.
  //
  // This was previously two separate effects (a "reset currentStep" effect
  // and a "navigate to step.route" effect). Because passive effects in the
  // same commit all read state from that render, the navigate effect fired
  // using the STALE currentStep (still the old value) before the reset
  // effect's setCurrentStep had a chance to apply — causing a real, if
  // momentary, incorrect navigate to /feed before self-correcting. Combining
  // them into one effect means the "just opened" branch computes and uses
  // the resolved starting step directly, so the initial navigation decision
  // is never made from a stale step.
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    let targetIndex = currentStep;
    if (justOpened) {
      const matchingStepIndex = tourSteps.findIndex((s) => s.route === location.pathname);
      targetIndex = matchingStepIndex >= 0 ? matchingStepIndex : 0;
      setCurrentStep(targetIndex);
      setSwipeDirection('right');
    }

    const targetRoute = tourSteps[targetIndex].route;
    if (targetRoute !== location.pathname) {
      navigate({ to: targetRoute });
    }
  }, [currentStep, isOpen, location.pathname, navigate]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setSwipeDirection('right');
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setSwipeDirection('left');
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleDragEnd = (_: unknown, info: { offset: { x: number }; velocity: { x: number } }) => {
    // The gesture started on an arrow: the arrow's own click owns it.
    if (arrowPressRef.current) {
      arrowPressRef.current = false;
      return;
    }

    const swipeThreshold = 50;
    const velocityThreshold = 500;

    if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      // Swiped left - go to next
      if (!isLastStep) {
        handleNext();
      } else {
        handleComplete();
      }
    } else if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      // Swiped right - go to previous
      if (!isFirstStep) {
        handlePrev();
      }
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Mark tour as complete in backend
    completeTour();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <MotionBox
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.800"
        zIndex={9999}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0}
        onDragEnd={handleDragEnd}
        // Capture phase runs before an arrow's own onPointerDown, so this
        // resets the flag for every press and the arrow then re-arms it.
        onPointerDownCapture={() => {
          arrowPressRef.current = false;
        }}
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        data-testid="onboarding-tour-overlay"
      >
        {/* Skip button */}
        <IconButton
          aria-label="Close tour"
          icon={<X size={24} />}
          position="absolute"
          top="calc(env(safe-area-inset-top, 0px) + 16px)"
          right={4}
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
          // Chakra's ghost _active bg is gray.200 in Light appearance, which
          // swallows the icon against this dark overlay.
          _active={{ bg: 'whiteAlpha.300' }}
          onClick={handleSkip}
          zIndex={10}
          data-testid="tour-skip"
        />

        {/* Tour content */}
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="100%"
          px={6}
          // Clear the notch (and the 40px close X pinned at top: 16px) and the home
          // indicator. On short phones the stack below
          // can't fit everything, so only the step content scrolls (see the
          // scroll region) while progress, arrows and Skip stay on screen.
          pt="calc(env(safe-area-inset-top, 0px) + 64px)"
          pb="calc(env(safe-area-inset-bottom, 0px) + 24px)"
        >
          <VStack
            spacing={6}
            width={'110%'}
            maxW="500px"
            maxH="100%"
            minH={0}
            textAlign="center"
            px={8}
            py={{ base: 2, md: 8 }}
          >
            {/* Progress bar */}
            <Box w="100%" flexShrink={0} data-testid="tour-progress">
              <Progress
                value={progress}
                size="sm"
                colorScheme="purple"
                borderRadius="full"
                bg="whiteAlpha.200"
              />
              <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                {currentStep + 1} of {tourSteps.length}
              </Text>
            </Box>

            {/* Step content — the only region that scrolls on short screens.
                overflowX is hidden so the slide transition doesn't add a
                horizontal scrollbar (overflow-y: auto forces overflow-x off
                `visible`). */}
            <Box flex="1 1 auto" minH={0} w="100%" overflowY="auto" overflowX="hidden">
            <AnimatePresence mode="wait">
              <MotionBox
                key={step.id}
                initial={{ opacity: 0, x: swipeDirection === 'right' ? 100 : -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: swipeDirection === 'right' ? -100 : 100 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                width="100%"
              >
                <VStack spacing={4} width="100%" pb={16}>
                  {/* Icon */}
                  <Box
                    p={4}
                    borderRadius="full"
                    bg="purple.500"
                    color="white"
                  >
                    {step.icon}
                  </Box>

                  {/* Title */}
                  <Heading size="md" color="white">
                    {step.title}
                  </Heading>

                  {/* Subtitle */}
                  <Text
                    fontSize="sm"
                    color="var(--color-secondary-green)"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    fontWeight="semibold"
                  >
                    {step.subtitle}
                  </Text>

                  {/* Description */}
                  <Text color="gray.300" lineHeight="1.7" whiteSpace="pre-line">
                    {step.description}
                  </Text>
                </VStack>
              </MotionBox>
            </AnimatePresence>
            </Box>

            {/* Mobile: Dot indicators and swipe hint */}
            {isMobile && (
              <VStack spacing={4} pt={4} flexShrink={0}>
                {/* Arrow controls flank the dots so phone-width members can step
                    without swiping. They reuse handlePrev/handleNext (the same
                    handlers as the desktop Back/Next buttons). On the last step the
                    forward arrow finishes the tour (same as handleNext / "Get Started").
                    Previous on step 1 uses aria-disabled rather than the native
                    disabled attribute so a focused arrow keeps focus instead of
                    dropping it to <body>. 44px tap targets. */}
                <HStack spacing={3}>
                  <IconButton
                    aria-label="Previous step"
                    icon={<ChevronLeft size={24} />}
                    variant="ghost"
                    color="gray.300"
                    boxSize="44px"
                    minW="44px"
                    onClick={handlePrev}
                    onPointerDown={() => {
                      arrowPressRef.current = true;
                    }}
                    aria-disabled={isFirstStep}
                    opacity={isFirstStep ? 0.4 : 1}
                    cursor={isFirstStep ? 'not-allowed' : 'pointer'}
                    _hover={isFirstStep ? undefined : { color: 'gray.100', bg: 'whiteAlpha.200' }}
                    _active={{ bg: 'whiteAlpha.300' }}
                    data-testid="tour-arrow-prev"
                  />
                  {/* Dot indicators */}
                  <HStack spacing={2}>
                    {tourSteps.map((_, index) => (
                      <Box
                        key={index}
                        w={index === currentStep ? '24px' : '8px'}
                        h="8px"
                        borderRadius="full"
                        bg={index === currentStep ? 'purple.500' : 'whiteAlpha.400'}
                        transition="all 0.2s"
                        cursor="pointer"
                        onClick={() => {
                          setSwipeDirection(index > currentStep ? 'right' : 'left');
                          setCurrentStep(index);
                        }}
                      />
                    ))}
                  </HStack>
                  <IconButton
                    aria-label={isLastStep ? 'Finish tour' : 'Next step'}
                    icon={<ChevronRight size={24} />}
                    variant="ghost"
                    color="gray.300"
                    boxSize="44px"
                    minW="44px"
                    onClick={handleNext}
                    onPointerDown={() => {
                      arrowPressRef.current = true;
                    }}
                    _hover={{ color: 'gray.100', bg: 'whiteAlpha.200' }}
                    _active={{ bg: 'whiteAlpha.300' }}
                    data-testid="tour-arrow-next"
                  />
                </HStack>

                {/* Swipe hint */}
                <Text fontSize="xs" color="gray.500">
                  {isLastStep ? 'Swipe left to finish' : 'Swipe to navigate'}
                </Text>

                {/* Finish button on last step */}
                {isLastStep && (
                  <Button
                    bg="purple.500"
                    color="white"
                    onClick={handleComplete}
                    _hover={{ bg: 'purple.600' }}
                    size="lg"
                    w="100%"
                    data-testid="tour-next"
                  >
                    Get Started
                  </Button>
                )}
              </VStack>
            )}

            {/* Desktop: Navigation buttons */}
            {!isMobile && (
              <HStack spacing={4} w="100%" pt={4} flexShrink={0}>
                <Button
                  flex={1}
                  variant="ghost"
                  color="gray.400"
                  leftIcon={<ChevronLeft size={20} />}
                  onClick={handlePrev}
                  isDisabled={isFirstStep}
                  _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                  _active={{ bg: 'whiteAlpha.300' }}
                >
                  Back
                </Button>
                <Button
                  flex={1}
                  bg="purple.500"
                  color="white"
                  rightIcon={<ChevronRight size={20} />}
                  onClick={handleNext}
                  _hover={{ bg: 'purple.600' }}
                  data-testid="tour-next"
                >
                  {isLastStep ? 'Finish' : 'Next'}
                </Button>
              </HStack>
            )}

            {/* Skip link */}
            {!isLastStep && (
              <Button
                variant="link"
                color="gray.500"
                fontSize="sm"
                flexShrink={0}
                onClick={handleSkip}
                _hover={{ color: 'gray.300' }}
              >
                Skip tour
              </Button>
            )}
          </VStack>
        </Flex>
      </MotionBox>
    </AnimatePresence>
  );
}

export default OnboardingTour;
